import React, { useState, useEffect, useRef } from 'react';
import { useInfiniteScroll } from '../hook/useInfiniteScroll';
import DropdownIcon from '../images/DropdownIcon';

interface DropdownSearchProps<T> {
    refKey: string;
    refs: { [key: string]: React.RefObject<HTMLDivElement> };
    openStates: { [key: string]: boolean };
    open: (key: string) => void;
    close: (key: string) => void;
    data: T[];
    labelExtractor: (item: T) => string;
    value: string;
    setValue: (v: string) => void;
    onSelect: (item: T) => void;
    onSearch?: (v: string) => void;
    placeholder?: string;
    error?: string;
    tabIndex?: number;
    onLoadMore?: () => void;
    hasMore?: boolean;
    loading?: boolean;
    borderNone?: boolean;
    inItem?: boolean;
    disabled?: boolean;
    /** When true, search is server-side only: no client filtering, list = data as-is. Parent handles fetch + debounce. */
    serverSideSearch?: boolean;
    /** Focus the input on mount — used to land the cursor on a freshly added row. */
    autoFocus?: boolean;
}

export function DropdownSearch<T>({
    refKey,
    refs,
    openStates,
    open,
    close,
    data = [],
    labelExtractor,
    value,
    setValue,
    onSelect,
    onSearch,
    placeholder,
    error,
    tabIndex = 0,
    onLoadMore,
    borderNone = false,
    hasMore = false,
    loading = false,
    inItem = false,
    disabled = false,
    serverSideSearch = false,
    autoFocus = false,
}: DropdownSearchProps<T>) {
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const dropdownScrollRef = useRef<HTMLDivElement>(null);
    const loaderElementRef = useRef<HTMLDivElement | null>(null);
    const [filtered, setFiltered] = useState<T[]>(data);
    const previousDataLengthRef = useRef<number>(data.length);
    const isLoadingMoreRef = useRef<boolean>(false);
    const selectedItemRef = useRef<T | null>(null);
    const hasUserInteractedRef = useRef<boolean>(false);

    const handleBlur = () => {
        close(refKey);
    };

    useEffect(() => {
        // Chỉ scroll vào item khi KHÔNG đang load more
        if (highlightedIndex !== -1 && itemRefs.current[highlightedIndex] && !isLoadingMoreRef.current) {
            itemRefs.current[highlightedIndex]?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });

            const isLastItem = highlightedIndex === filtered.length - 1;
            if (isLastItem && hasMore && !loading && onLoadMore) {
                isLoadingMoreRef.current = true;
                setTimeout(() => {
                    if (loaderElementRef.current && dropdownScrollRef.current) {
                        const loaderRect = loaderElementRef.current.getBoundingClientRect();
                        const dropdownRect = dropdownScrollRef.current.getBoundingClientRect();
                        if (loaderRect.top < dropdownRect.bottom) {
                            onLoadMore();
                        }
                    }
                }, 100);
            }
        }
    }, [highlightedIndex, hasMore, loading, onLoadMore, filtered.length]);

    const pick = (item: T) => {
        selectedItemRef.current = item;
        hasUserInteractedRef.current = false;
        onSelect(item);
        setValue(labelExtractor(item));
        close(refKey);
    };

    // Keyboard: arrows open and move, Tab picks the highlighted item and moves on, Enter picks it
    // (or the exact match for typed text) and never leaves an open list; Escape closes.
    const handleKeyDown = (e: any) => {
        if (disabled) return;
        const isOpen = !!openStates[refKey];

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isOpen) open(refKey);
            setHighlightedIndex((prev) =>
                e.key === 'ArrowDown' ? Math.min(filtered.length - 1, prev + 1) : Math.max(0, prev - 1),
            );
        } else if (e.key === 'Tab' && isOpen && highlightedIndex !== -1 && filtered[highlightedIndex]) {
            pick(filtered[highlightedIndex]);
        } else if (e.key === 'Escape' && isOpen) {
            e.preventDefault();
            e.stopPropagation();
            close(refKey);
        } else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
            const typed = hasUserInteractedRef.current && String(value ?? '').trim() !== '';
            // A closed list with nothing newly typed leaves Enter to the form, so it can save.
            if (!isOpen && !typed) return;
            e.preventDefault();
            e.stopPropagation();
            if (isOpen && highlightedIndex !== -1 && filtered[highlightedIndex]) {
                pick(filtered[highlightedIndex]);
                return;
            }
            if (typed) {
                // Only an exact name is picked: a lone result may be left over from an earlier search.
                const term = String(value).trim().toLowerCase();
                const exact = filtered.find((item) => String(labelExtractor(item)).trim().toLowerCase() === term);
                if (exact) pick(exact);
                else {
                    if (!isOpen) open(refKey);
                    if (onSearch) onSearch(String(value).trim());
                }
                return;
            }
            close(refKey);
        }
    };

    const handleLoadMore = () => {
        if (openStates[refKey] && onLoadMore) {
            onLoadMore();
        }
    };

    const { setLoaderRef } = useInfiniteScroll({
        hasMore: openStates[refKey] && hasMore,
        loading,
        onLoadMore: handleLoadMore,
        scrollRootRef: dropdownScrollRef,
    });

    useEffect(() => {
        if (openStates[refKey]) {
            setHighlightedIndex(-1);
        }
    }, [openStates, refKey]);

    useEffect(() => {
        if (value && !selectedItemRef.current) {
            const matchedItem = data.find(item => labelExtractor(item) === value);
            if (matchedItem) {
                selectedItemRef.current = matchedItem;
            }
        }
    }, [value, data, labelExtractor]);

    useEffect(() => {
        if (openStates[refKey]) {
            setHighlightedIndex(-1);
            if (value) {
                const matchedItem = data.find(item => labelExtractor(item) === value);
                if (matchedItem) {
                    selectedItemRef.current = matchedItem;
                }
            }
        }
    }, [openStates, refKey, value, data, labelExtractor]);

    useEffect(() => {
        if (serverSideSearch) {
            // Server-side search: no client filtering, list = data as-is
            setFiltered(data);
        } else {
            // Client-side: filter by value when open and user has interacted
            if (openStates[refKey] && hasUserInteractedRef.current) {
                if (!value) {
                    setFiltered(data);
                } else {
                    const searchLower = value.toLowerCase();
                    const filteredData = data.filter((item) => {
                        const label = labelExtractor(item).toLowerCase();
                        return label.includes(searchLower);
                    });
                    setFiltered(filteredData);
                }
            } else {
                setFiltered(data);
            }
        }

        if (data.length > previousDataLengthRef.current) {
            isLoadingMoreRef.current = false;
        }
        previousDataLengthRef.current = data.length;
    }, [data, value, labelExtractor, openStates, refKey, serverSideSearch]);

    useEffect(() => {
        if (selectedItemRef.current && labelExtractor(selectedItemRef.current) !== value) {
            selectedItemRef.current = null;
        }
    }, [value, labelExtractor]);

    return (
        <>
            <div className="relative w-full h-full" ref={refs[refKey]}>
                <div className="flex items-center justify-center absolute" style={{ width: '24px', height: '24px', top: '50%', transform: 'translateY(-50%)', right: '8px' }}>
                    <DropdownIcon />
                </div>
                <input
                    autoFocus={autoFocus}
                    tabIndex={tabIndex}
                    type="text"
                    disabled={disabled}
                    value={value}
                    onChange={(e) => {
                        if (!disabled) {
                            hasUserInteractedRef.current = true;
                            const v = e.target.value;
                            setValue(v);
                            // Typing after a pick or Escape shows the list again.
                            if (!openStates[refKey]) open(refKey);
                            if (serverSideSearch && onSearch) {
                                onSearch(v);
                            }
                        }
                    }}
                    role="combobox"
                    aria-expanded={!!openStates[refKey] && !disabled}
                    aria-autocomplete="list"
                    onFocus={() => !disabled && open(refKey)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    style={{
                        color: "#4A5353",
                        fontSize: "14px",
                        fontWeight: 500,
                        pointerEvents: disabled ? 'none' : 'all',
                        cursor: disabled ? 'not-allowed' : 'text',
                        opacity: disabled ? 0.6 : 1
                    }}
                    className={`h-full font-inter placeholder:text-placeholder ${borderNone === false
                        ? "border border-[#EBEBED] p-3"
                        : "border-none w-full"
                        } rounded-lg outline-none focus:outline-none bg-transparent w-full`}
                />


                {openStates[refKey] && !disabled && (
                    <div
                        ref={dropdownScrollRef}
                        className="shadow-dropdown absolute z-10 w-full mt-1 bg-white border border-[#EBEBED] rounded-md shadow-lg max-h-60 overflow-auto scroll-container"
                    >
                        {loading && filtered.length === 0 ? (
                            <div style={{ padding: '16px' }} className="px-4 py-8 flex items-center justify-center">
                                <span className="loader"></span>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div style={{ padding: '16px' }} className="px-4 py-8 text-center text-gray-500">
                                <p>No items found</p>
                            </div>
                        ) : (
                            <>
                                {filtered.map((item, index) => (
                                    <div
                                        key={index}
                                        ref={(el) => (itemRefs.current[index] = el)}
                                        style={{
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            color: "#4A5353"
                                        }}
                                        className={`px-4 py-2 cursor-pointer font-inter ${highlightedIndex === index ? 'bg-[#E7F1F1]' : ''
                                            }`}
                                        onMouseDown={() => {
                                            selectedItemRef.current = item;
                                            hasUserInteractedRef.current = false;
                                            onSelect(item);
                                            setValue(labelExtractor(item));
                                            close(refKey);
                                        }}
                                    >
                                        {labelExtractor(item)}
                                    </div>
                                ))}

                                {hasMore && (
                                    <div
                                        ref={(el) => {
                                            setLoaderRef(el);
                                            loaderElementRef.current = el;
                                        }}
                                        style={{ height: "1px" }}
                                    >
                                        {loading && (
                                            <div className="w-full py-2 flex items-center justify-center">
                                                <span className="loader"></span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            {error && <p className="error">{error}</p>}
        </>
    );
}